import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  LayoutDashboard, ShieldCheck, Cpu, Mail, CalendarClock, ClipboardList, Users,
  Plane, FlaskConical, FileText, Wand2, Brain, Mic, Send, Command, Loader2, Sparkles, Fingerprint, Link2, Bell, X, LogOut,
} from "lucide-react";
import { getBootstrap, transcribe, getProactive, speak, authLogout } from "../lib/api";
import AriaConsole from "../components/office/AriaConsole";
import Onboarding from "../components/office/Onboarding";
import CreativeStudio from "../components/office/CreativeStudio";
import MemoryPanel from "../components/office/MemoryPanel";
import StyleClone from "../components/office/StyleClone";
import ConnectorsHub from "../components/office/ConnectorsHub";
import {
  TodaySection, DecisionsSection, AgentsSection, EmailSection, MeetingsSection,
  TasksSection, PeopleSection, TravelSection, ResearchSection, DocumentsSection,
} from "../components/office/Sections";
import { toast, Toaster } from "sonner";

const TABS = [
  { key: "today", label: "Today", icon: LayoutDashboard },
  { key: "decisions", label: "Decisions", icon: ShieldCheck },
  { key: "agents", label: "Agents", icon: Cpu },
  { key: "email", label: "Email", icon: Mail },
  { key: "meetings", label: "Meetings", icon: CalendarClock },
  { key: "tasks", label: "Tasks", icon: ClipboardList },
  { key: "people", label: "People", icon: Users },
  { key: "travel", label: "Travel", icon: Plane },
  { key: "research", label: "Research", icon: FlaskConical },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "creative", label: "Creative Studio", icon: Wand2 },
  { key: "voice", label: "My Voice", icon: Fingerprint },
  { key: "connections", label: "Connections", icon: Link2 },
  { key: "memory", label: "Memory", icon: Brain },
];

const LANGS = [
  { code: "en", label: "EN" },
  { code: "hi", label: "हि" },
  { code: "mr", label: "म" },
];

function useRecorder(onResult, language) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const toggle = useCallback(async () => {
    if (recording) {
      mediaRef.current && mediaRef.current.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        setBusy(true);
        try {
          const blob = new Blob(chunksRef.current, { type: "audio/webm" });
          const text = await transcribe(blob, language === "auto" ? "" : language);
          if (text && text.trim()) onResult(text.trim());
          else toast.error("Didn't catch that. Please try again.");
        } catch (e) {
          toast.error("Transcription failed.");
        }
        setBusy(false);
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error("Microphone access denied.");
    }
  }, [recording, onResult, language]);

  return { recording, busy, toggle };
}

export default function ExecutiveOffice({ user }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("today");
  const [language, setLanguage] = useState("en");
  const [command, setCommand] = useState("");
  const [pending, setPending] = useState(null);
  const [proactive, setProactive] = useState({ items: [], count: 0 });
  const [showProactive, setShowProactive] = useState(false);
  const ariaRef = useRef(null);
  const greetedRef = useRef(false);

  const load = useCallback(async () => {
    const d = await getBootstrap();
    setData(d);
    getProactive().then(setProactive).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // Aria greets aloud on first interaction each morning (browsers block autoplay pre-gesture)
  useEffect(() => {
    if (!data?.profile) return;
    if (data.profile.auto_greet === false) return;
    const greet = async () => {
      if (greetedRef.current) return;
      greetedRef.current = true;
      window.removeEventListener("pointerdown", greet);
      const hour = new Date().getHours();
      const period = hour < 12 ? "morning" : hour < 17 ? "afternoon" : "evening";
      try {
        const line = `Good ${period}, ${data.profile.name || "Kaustubh"}. I'm Aria. I've prepared your day and I'm ready when you are.`;
        const audio = await speak(line, data.profile.voice || "nova", data.profile.voice_speed || 1.0);
        const a = new Audio(audio);
        a.play().catch(() => {});
      } catch (e) {}
    };
    window.addEventListener("pointerdown", greet);
    return () => window.removeEventListener("pointerdown", greet);
  }, [data]);

  const ask = useCallback((text, agent = "chief_of_staff") => {
    setTab("today");
    if (ariaRef.current) ariaRef.current.send(text, agent);
    else setPending({ text, agent, n: Date.now() });
  }, []);

  const submitCommand = () => {
    if (!command.trim()) return;
    ask(command);
    setCommand("");
  };

  const barRec = useRecorder((t) => setCommand(t), language);
  const floatRec = useRecorder((t) => ask(t), language);

  const onDecision = (dec) => {
    toast.success(`New Level ${dec.level} approval added to Decisions`);
    load();
  };

  if (!data) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#07090E]">
        <div className="flex items-center gap-3 text-amber-400"><Loader2 className="animate-spin" /> Opening your executive office…</div>
      </div>
    );
  }

  const renderSection = () => {
    switch (tab) {
      case "decisions": return <DecisionsSection data={data} refresh={load} />;
      case "agents": return <AgentsSection data={data} onAsk={ask} />;
      case "email": return <EmailSection data={data} onAsk={ask} />;
      case "meetings": return <MeetingsSection data={data} onAsk={ask} />;
      case "tasks": return <TasksSection data={data} />;
      case "people": return <PeopleSection data={data} onAsk={ask} refresh={load} />;
      case "travel": return <TravelSection data={data} onAsk={ask} />;
      case "research": return <ResearchSection data={data} onAsk={ask} />;
      case "documents": return <DocumentsSection data={data} onAsk={ask} />;
      case "creative": return <CreativeStudio />;
      case "voice": return <StyleClone />;
      case "connections": return <ConnectorsHub />;
      case "memory": return <MemoryPanel />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#07090E] grain relative">
      <Toaster theme="dark" position="top-center" richColors />
      {data.profile && !data.profile.onboarded && <Onboarding onDone={load} />}

      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex flex-col fixed left-0 top-0 h-full w-64 glass border-r border-white/10 z-30">
        <div className="px-6 py-7 border-b border-amber-500/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl gold-btn grid place-items-center"><Sparkles size={18} /></div>
            <div>
              <div className="font-display text-xl font-semibold leading-tight text-amber-50/95">{data.profile?.name}'s Office</div>
              <div className="overline text-[9px] mt-0.5">Executive Operating System</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-5 px-3.5 space-y-0.5">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm transition-all duration-300 ${
                tab === t.key ? "bg-amber-500/12 text-amber-200 border border-amber-500/25 shadow-[0_0_24px_-10px_rgba(226,184,87,0.5)]" : "text-slate-400 hover:text-amber-100 hover:bg-amber-500/[0.04] border border-transparent"
              }`}
              data-testid={`tab-${t.key}`}>
              <t.icon size={16} strokeWidth={1.75} /> <span className="tracking-wide">{t.label}</span>
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-amber-500/10">
          <div className="flex items-center gap-2.5">
            {user?.picture ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover border border-amber-400/30" /> : <div className="w-8 h-8 rounded-full bg-amber-500/20" />}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name || "You"}</div>
              <div className="text-[10px] text-slate-500 truncate">{user?.email}</div>
            </div>
            <button onClick={async () => { await authLogout(); window.location.href = "/"; }} className="p-1.5 rounded-lg text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition" title="Sign out" data-testid="logout-button">
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="lg:ml-64">
        {/* Top bar */}
        <header className="sticky top-0 z-20 glass border-b border-white/10 px-4 sm:px-6 py-3">
          <div className="flex items-center gap-3">
            <div className="lg:hidden flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg gold-btn grid place-items-center"><Sparkles size={16} /></div>
            </div>
            <div className="flex-1 flex items-center gap-2 glass-card rounded-xl px-3 py-2">
              <Command size={15} className="text-amber-400 shrink-0" />
              <input
                value={command}
                onChange={(e) => setCommand(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitCommand()}
                placeholder="Ask Kaustubh's Office anything…"
                className="flex-1 bg-transparent text-sm focus:outline-none min-w-0"
                data-testid="universal-command-bar"
              />
              <button onClick={barRec.toggle} className={`p-1.5 rounded-lg transition ${barRec.recording ? "text-rose-400 mic-live" : "text-slate-400 hover:text-amber-300"}`} data-testid="command-bar-mic">
                {barRec.busy ? <Loader2 size={15} className="animate-spin" /> : <Mic size={15} />}
              </button>
              <button onClick={submitCommand} className="gold-btn rounded-lg p-1.5" data-testid="command-bar-submit">
                <Send size={15} />
              </button>
            </div>
            <div className="flex items-center gap-1 glass-card rounded-xl p-1">
              {LANGS.map((l) => (
                <button key={l.code} onClick={() => setLanguage(l.code)}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-bold transition ${language === l.code ? "gold-btn" : "text-slate-400 hover:text-amber-300"}`}
                  data-testid={`lang-${l.code}`}>
                  {l.label}
                </button>
              ))}
            </div>
            <button onClick={() => setShowProactive(true)} className="relative glass-card rounded-xl p-2.5 text-amber-300/90 hover:text-amber-200 transition" data-testid="proactive-bell" title="What Aria noticed">
              <Bell size={16} />
              {proactive.count > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold grid place-items-center">{proactive.count}</span>
              )}
            </button>
            <button onClick={() => setTab("today")} className="hidden sm:flex items-center gap-2 gold-btn px-3.5 py-2 rounded-xl text-sm" data-testid="open-chief-of-staff">
              <Sparkles size={15} /> Talk to Aria
            </button>
          </div>

          {/* Mobile tab scroller */}
          <div className="lg:hidden mt-3 -mx-4 px-4 flex gap-2 overflow-x-auto pb-1">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition ${tab === t.key ? "gold-btn" : "border border-white/10 text-slate-400"}`}
                data-testid={`mtab-${t.key}`}>
                <t.icon size={13} /> {t.label}
              </button>
            ))}
          </div>
        </header>

        <main className="px-4 sm:px-6 lg:px-10 py-6 lg:py-8 max-w-6xl mx-auto pb-28">
          <div className={tab === "today" ? "" : "hidden"}>
            <AriaConsole ref={ariaRef} language={language} onDecision={onDecision} name={data.profile?.name} pending={pending} voice={data.profile?.voice || "nova"} speed={data.profile?.voice_speed || 1.0} />
          </div>
          {tab !== "today" && <div key={tab}>{renderSection()}</div>}
        </main>
      </div>

      {/* Proactive intelligence panel */}
      <div className={`fixed top-0 right-0 h-full w-full sm:w-[400px] z-50 glass border-l border-amber-500/15 flex flex-col transition-transform duration-300 ${showProactive ? "translate-x-0" : "translate-x-full"}`} data-testid="proactive-panel">
        <div className="flex items-center justify-between px-5 py-4 border-b border-amber-500/10">
          <div>
            <div className="overline">Aria noticed</div>
            <div className="font-display text-xl font-medium text-amber-50/95">Before you ask</div>
          </div>
          <button onClick={() => setShowProactive(false)} className="p-2 rounded-lg hover:bg-white/10 transition" data-testid="close-proactive"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
          {proactive.items.length === 0 && (
            <div className="text-center pt-16 px-6">
              <Bell size={28} className="mx-auto mb-3 text-amber-400/50" />
              <p className="text-slate-400 text-sm font-light">All clear. Nothing needs your attention yet — as your office fills with real work, I'll flag deadlines, unanswered messages and birthdays here on my own.</p>
            </div>
          )}
          {proactive.items.map((it, i) => {
            const tone = it.urgency === "high" ? "#F43F5E" : it.urgency === "medium" ? "#E2B857" : "#38BDF8";
            return (
              <button key={i} onClick={() => { setShowProactive(false); ask(`About this: ${it.title}. ${it.detail}. What do you recommend and can you handle it?`); }}
                className="w-full text-left glass-card p-4 group" data-testid="proactive-item">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: tone }} />
                  <span className="text-[10px] uppercase tracking-wider" style={{ color: tone }}>{it.type}</span>
                </div>
                <div className="text-sm font-medium mt-1.5 group-hover:text-amber-200 transition">{it.title}</div>
                <div className="text-[12px] text-slate-500 mt-0.5">{it.detail}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Persistent floating mic */}
      <button
        onClick={floatRec.toggle}
        className={`fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full gold-btn grid place-items-center shadow-lg ${floatRec.recording ? "mic-live" : ""}`}
        data-testid="persistent-mic-button"
        title="Speak to Aria"
      >
        {floatRec.busy ? <Loader2 size={22} className="animate-spin" /> : <Mic size={22} />}
      </button>
    </div>
  );
}
